import {useState} from 'react';
import {useNavigate} from 'react-router';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {displayCountryName} from '@common/ui/country/country-display';
import {useSettings} from '@ui/settings/use-settings';
import type {BlockInstance} from '../appearance-data';

type P = {block: BlockInstance; props: Record<string, any>};

// tab key → public search endpoint + detail-page route segment.
const TAB_ENDPOINT: Record<string, string> = {
  scholarship: 'public/opp/scholarships', university: 'public/opp/universities',
  course: 'public/opp/courses', job: 'public/opp/jobs', program: 'public/opp/programs',
};
const TAB_ROUTE: Record<string, string> = {
  scholarship: 'scholarships', university: 'universities', course: 'courses', job: 'jobs', program: 'programs',
};
function resultTitle(it: any): string {
  return it?.translation?.title || it?.translation?.name || it?.title || it?.name || it?.slug || `#${it?.id}`;
}
function resultLogo(it: any): string | null {
  return it?.provider_logo_url || it?.logo_url || it?.logo || it?.company?.logo_url || it?.cover_image_url || it?.image || null;
}

/**
 * Tabbed "Advanced Search" panel — a compact front door to /opportunities.
 *
 * Everything here mirrors opp-search-page.tsx: the same tabs in the same order
 * (the `opportunities.tab_order` setting, types with no listings hidden), and
 * per tab the same filters in the same order, labels and option values as its
 * sidebar. Only filters the search page hydrates from the URL are offered —
 * anything else would be silently dropped on arrival.
 */

type TabType = 'university' | 'scholarship' | 'course' | 'job' | 'program';

// Same labels + fallback order as ALL_TABS / FALLBACK_TAB_ORDER on the search page.
const ALL_TABS: Record<TabType, string> = {
  scholarship: 'Scholarship',
  university: 'Universities',
  program: 'Programs',
  course: 'Courses',
  job: 'Jobs',
};
const FALLBACK_TAB_ORDER: TabType[] = ['scholarship', 'university', 'program', 'course', 'job'];

// Identical to tabsFromSetting() on the search page: unknown keys dropped, any
// tab the setting forgets appended.
function tabsFromSetting(raw?: string | null): TabType[] {
  const keys = String(raw ?? '')
    .split(',')
    .map(k => k.trim())
    .filter((k): k is TabType => k in ALL_TABS);
  return [...new Set([...(keys.length ? keys : FALLBACK_TAB_ORDER), ...FALLBACK_TAB_ORDER])];
}

// A single select filter: the query param it emits, its label, and its options.
// The "All" option is added at render time, as the search page's Sel does.
interface Filter {
  param: string;
  label: string;
  options: {value: string; label: string}[];
}

// Pseudo-filter marking where Destination (country) sits in a tab's order —
// program leads with Degree Level, every other tab with Destination.
const DESTINATION = 'country_code';

const F = {
  degreeLevel: {
    param: 'degree_level',
    label: 'Degree Level',
    options: [
      {value: 'high_school', label: 'High school'},
      {value: 'bachelor', label: 'Bachelor'},
      {value: 'master', label: 'Master'},
      {value: 'phd', label: 'PhD'},
      {value: 'diploma', label: 'Diploma'},
      {value: 'associate', label: 'Associate'},
      {value: 'certificate', label: 'Certificate'},
    ],
  },
  appStatus: {
    param: 'app_status',
    label: 'Application status',
    options: [
      {value: 'open', label: 'Applications open'},
      {value: 'closing_soon', label: 'Closing soon'},
      {value: 'opens_soon', label: 'Opens soon'},
      {value: 'closed', label: 'Closed'},
    ],
  },
  universityType: {
    param: 'university_type',
    label: 'University Type',
    options: [
      {value: 'public', label: 'Public'},
      {value: 'private', label: 'Private'},
      {value: 'nonprofit', label: 'Nonprofit'},
      {value: 'online', label: 'Online'},
    ],
  },
  uniLanguage: {
    param: 'study_language',
    label: 'Medium of Language',
    options: [
      {value: 'en', label: 'English'},
      {value: 'ar', label: 'Arabic'},
      {value: 'tr', label: 'Turkish'},
      {value: 'zh', label: 'Chinese'},
      {value: 'fr', label: 'French'},
      {value: 'de', label: 'German'},
      {value: 'es', label: 'Spanish'},
    ],
  },
  funding: {
    param: 'type',
    label: 'Funding Type',
    options: [
      {value: 'full', label: 'Fully Funded'},
      {value: 'partial', label: 'Partial'},
      {value: 'tuition_only', label: 'Tuition Only'},
      {value: 'volunteer', label: 'Volunteering'},
      {value: 'training', label: 'Training'},
      {value: 'living_only', label: 'Living Only'},
      {value: 'research', label: 'Research'},
      {value: 'exchange', label: 'Exchange'},
    ],
  },
  scholarshipLevel: {
    param: 'level',
    label: 'Level',
    options: [
      {value: 'bachelor', label: 'Bachelor'},
      {value: 'master', label: 'Master'},
      {value: 'phd', label: 'PhD'},
      {value: 'postdoc', label: 'Postdoc'},
    ],
  },
  courseLevel: {
    param: 'level',
    label: 'Level',
    options: [
      {value: 'beginner', label: 'Beginner'},
      {value: 'intermediate', label: 'Intermediate'},
      {value: 'advanced', label: 'Advanced'},
      {value: 'all_levels', label: 'All Levels'},
    ],
  },
  deliveryMode: {
    param: 'delivery_mode',
    label: 'Delivery Mode',
    options: [
      {value: 'self_paced', label: 'Self Paced'},
      {value: 'instructor_led', label: 'Instructor Led'},
      {value: 'hybrid', label: 'Hybrid'},
      {value: 'live_virtual', label: 'Live Virtual'},
    ],
  },
  courseLanguage: {
    param: 'language',
    label: 'Language',
    options: [
      {value: 'en', label: 'English'},
      {value: 'ar', label: 'Arabic'},
      {value: 'tr', label: 'Turkish'},
      {value: 'fr', label: 'French'},
      {value: 'de', label: 'German'},
    ],
  },
  certificateType: {
    param: 'certificate_type',
    label: 'Certificate Type',
    options: [
      {value: 'completion', label: 'Completion'},
      {value: 'professional', label: 'Professional'},
      {value: 'accredited', label: 'Accredited'},
      {value: 'none', label: 'None'},
    ],
  },
  jobType: {
    param: 'job_type',
    label: 'Job Type',
    options: [
      {value: 'full_time', label: 'Full Time'},
      {value: 'part_time', label: 'Part Time'},
      {value: 'contract', label: 'Contract'},
      {value: 'internship', label: 'Internship'},
      {value: 'freelance', label: 'Freelance'},
      {value: 'remote', label: 'Remote'},
    ],
  },
  experienceLevel: {
    param: 'experience_level',
    label: 'Experience Level',
    options: [
      {value: 'entry', label: 'Entry Level'},
      {value: 'mid', label: 'Mid Level'},
      {value: 'senior', label: 'Senior'},
      {value: 'lead', label: 'Lead'},
      {value: 'executive', label: 'Executive'},
    ],
  },
  remoteType: {
    param: 'remote_type',
    label: 'Remote Type',
    options: [
      {value: 'on_site', label: 'On Site'},
      {value: 'hybrid', label: 'Hybrid'},
      {value: 'fully_remote', label: 'Fully Remote'},
    ],
  },
  salaryPeriod: {
    param: 'salary_period',
    label: 'Salary Period',
    options: [
      {value: 'hourly', label: 'Hourly'},
      {value: 'monthly', label: 'Monthly'},
      {value: 'yearly', label: 'Yearly'},
      {value: 'project', label: 'Project'},
    ],
  },
  studyType: {
    param: 'study_type',
    label: 'Study Type',
    options: [
      {value: 'full_time', label: 'Full Time'},
      {value: 'part_time', label: 'Part Time'},
      {value: 'online', label: 'Online'},
      {value: 'hybrid', label: 'Hybrid'},
    ],
  },
  programLanguage: {
    param: 'study_language',
    label: 'Language of Program',
    options: [
      {value: 'en', label: 'English'},
      {value: 'tr', label: 'Turkish'},
      {value: 'ar', label: 'Arabic'},
      {value: 'fr', label: 'French'},
      {value: 'de', label: 'German'},
      {value: 'es', label: 'Spanish'},
      {value: 'it', label: 'Italian'},
      {value: 'ru', label: 'Russian'},
      {value: 'fa', label: 'Persian'},
      {value: 'zh', label: 'Chinese'},
    ],
  },
} satisfies Record<string, Filter>;

// Per tab, in the search page sidebar's order. Left out on purpose: the fee
// range, University/Speciality pickers, nationality, age, GPA and the
// checkboxes — the search page does not read those from the URL. (Scholarships
// need no `open_only`: the search page forces it on that tab itself.)
const TAB_CONFIG: Record<TabType, {
  /** What the box searches on THIS tab. A single generic placeholder across
   *  five tabs tells somebody who has just picked "Jobs" nothing about what
   *  they are now searching. */
  placeholder: string;
  filters: (Filter | typeof DESTINATION)[];
}> = {
  scholarship: {
    placeholder: 'Search by scholarship name or provider…',
    filters: [DESTINATION, F.funding, F.scholarshipLevel],
  },
  university: {
    placeholder: 'Search by university name or city…',
    filters: [DESTINATION, F.appStatus, F.universityType, F.uniLanguage],
  },
  program: {
    placeholder: 'Search by programme or field of study…',
    filters: [F.degreeLevel, DESTINATION, F.appStatus, F.studyType, F.programLanguage],
  },
  course: {
    placeholder: 'Search by course title or provider…',
    filters: [DESTINATION, F.courseLevel, F.deliveryMode, F.courseLanguage, F.certificateType],
  },
  job: {
    placeholder: 'Search by job title or company…',
    filters: [DESTINATION, F.jobType, F.experienceLevel, F.remoteType, F.salaryPeriod],
  },
};

// Real ISO country codes — names resolved + localized via Intl.DisplayNames.
// A fixed list rather than the search page's live one: that comes from
// endpoints of 1.4 MB / 3 MB, too heavy to load on every homepage view.
const COUNTRY_CODES = ['', 'TR', 'DE', 'US', 'GB', 'CA', 'AU', 'FR', 'NL', 'ES', 'IT', 'PL', 'CN', 'RU'];

export function AdvancedSearchBlock({props}: P) {
  const navigate = useNavigate();
  const {trans} = useTrans();
  const {localeCode} = useSelectedLocale();
  const {opportunities: oppSettings} = useSettings() as any;
  const [activeKey, setActiveKey] = useState<TabType | null>(null);
  const [query, setQuery] = useState('');
  const [showMatches, setShowMatches] = useState(false);
  const [country, setCountry] = useState('');
  // Filter values keyed by their query param (reset when switching tabs).
  const [values, setValues] = useState<Record<string, string>>({});

  // Via `displayCountryName` for the NAME_OVERRIDES table — a direct
  // Intl.DisplayNames call renders "China mainland" for CN on newer ICU.
  const loc = String(localeCode || 'en').toLowerCase().split('-')[0];
  const regionNames = {of: (c: string) => displayCountryName(c, c, loc)};

  // Same query (and cache key) as the search page: a type with no listings at
  // all is hidden there, so it is hidden here rather than leading to an empty tab.
  const {data: tabAvailable} = useQuery({
    queryKey: ['opp-tab-availability'],
    queryFn: () =>
      apiClient
        .get('public/opp/search-counts')
        .then(r => (r.data?.available ?? {}) as Partial<Record<TabType, boolean>>),
    staleTime: 5 * 60_000,
  });
  const allTabKeys = tabsFromSetting(oppSettings?.tab_order);
  const availableKeys = allTabKeys.filter(k => tabAvailable?.[k] !== false);
  // Never render with no tab at all, whatever the availability call says.
  const tabKeys = availableKeys.length ? availableKeys : allTabKeys;
  // Default = first tab in the setting's order, as on the search page.
  const activeTab: TabType = activeKey && tabKeys.includes(activeKey) ? activeKey : tabKeys[0];
  const tab = {key: activeTab, ...TAB_CONFIG[activeTab]};
  const selectFilters = tab.filters.filter((f): f is Filter => f !== DESTINATION);

  // Typeahead: as you type, find matching listings of the active tab (name + logo).
  //
  // `lang` is not optional. Every listing endpoint reads the language from the
  // QUERY STRING — not Accept-Language, not the app locale — and falls back to
  // English. This call talks to the API directly rather than through the
  // `useOpp*` hooks, which pass it for you, so it was the one place on the
  // site still asking for English: an Arabic reader typing "إسطنبول" got
  // "Istanbul Gelisim University" back for a university that has had an Arabic
  // name all along.
  //
  // It is in the key as well as the params, so switching language refetches
  // instead of showing the previous language out of cache.
  const {data: matches} = useQuery({
    queryKey: ['adv-search-typeahead', tab.key, query.trim(), localeCode],
    queryFn: () =>
      apiClient
        .get(TAB_ENDPOINT[tab.key], {
          params: {query: query.trim(), perPage: 6, lang: localeCode},
        })
        .then(r => r.data.pagination.data as any[]),
    enabled: query.trim().length >= 2,
  });

  const switchTab = (key: TabType) => {
    setActiveKey(key);
    setValues({}); // filters differ per tab — clear stale values
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams({tab: tab.key});
    if (query.trim()) p.set('query', query.trim());
    if (country) p.set('country_code', country);
    selectFilters.forEach(f => {
      const v = values[f.param];
      if (v) p.set(f.param, v);
    });
    navigate(`/opportunities?${p.toString()}`);
  };

  const selectCls =
    'h-44 w-full rounded-xl border border-divider bg-paper px-12 text-sm outline-none focus:border-primary';

  return (
    // Pulled up so the card overlaps the section above it (the hero) — the
    // floating "search console" look. `relative z-10` keeps it above the hero's
    // decorative orbs rather than behind them.
    <section className="relative z-10 -mt-32 px-24 py-40 md:-mt-48"
      style={{backgroundColor: props.bg_color || 'transparent'}}>
      <div className="mx-auto max-w-[1100px] rounded-3xl border border-divider bg-paper p-20 shadow-2xl ring-1 ring-black/5 md:p-28">
        {props.heading ? (
          <h2 className="mb-20 text-center text-xl font-bold md:text-2xl">{props.heading}</h2>
        ) : null}

        {/* Type tabs */}
        <div className="mb-20 flex flex-wrap justify-center gap-8">
          {tabKeys.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => switchTab(key)}
              className={`flex items-center gap-6 rounded-full px-16 py-8 text-sm font-medium transition ${
                key === activeTab
                  ? 'bg-primary text-white shadow'
                  : 'bg-alt text-muted hover:bg-primary/10 hover:text-primary'
              }`}
            >
              <Trans message={ALL_TABS[key]} />
            </button>
          ))}
        </div>

        {/* Query + per-tab filters */}
        <form onSubmit={submit} className="flex flex-col gap-12">
          {/* The admin's own placeholder still wins where one is set on the
              block; otherwise it follows the active tab. */}
          <div className="relative">
            <div className="flex items-center gap-8 rounded-xl border border-divider bg-paper px-12 focus-within:border-primary">
              <span className="material-icons text-muted">search</span>
              <input
                value={query}
                onChange={e => { setQuery(e.target.value); setShowMatches(true); }}
                onFocus={() => setShowMatches(true)}
                placeholder={props.placeholder || trans({message: tab.placeholder})}
                className="h-48 flex-1 border-0 bg-transparent text-sm outline-none"
              />
            </div>
            {/* Live matches for the active tab (name + logo) */}
            {showMatches && query.trim().length >= 2 && (matches?.length ?? 0) > 0 && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMatches(false)} />
                <div
                  className="absolute inset-x-0 top-full z-40 mt-4 max-h-280 overflow-y-auto rounded-xl border border-divider shadow-xl"
                  style={{backgroundColor: 'var(--be-paper, #fff)'}}
                >
                  {(matches ?? []).map(it => {
                    const logo = resultLogo(it);
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => navigate(`/opportunities/${TAB_ROUTE[tab.key]}/${it.slug}`)}
                        className="flex w-full items-center gap-10 px-14 py-10 text-left hover:bg-alt"
                      >
                        {logo ? (
                          <img src={logo} alt="" className="h-32 w-32 shrink-0 rounded-lg object-contain" loading="lazy" />
                        ) : (
                          <span className="flex h-32 w-32 shrink-0 items-center justify-center rounded-lg bg-chip text-muted">
                            <span className="material-icons text-base">school</span>
                          </span>
                        )}
                        <span className="truncate text-sm font-medium">{resultTitle(it)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 md:grid-cols-3">
            {/* Per-tab filters, Destination included, in the search page's order */}
            {tab.filters.map(f =>
              f === DESTINATION ? (
                <LabeledSelect key={f} label="Destination" value={country} onChange={setCountry} cls={selectCls}>
                  {COUNTRY_CODES.map(code => (
                    <option key={code} value={code}>
                      {code === '' ? trans({message: 'All Countries'}) : regionNames?.of(code) || code}
                    </option>
                  ))}
                </LabeledSelect>
              ) : (
                <LabeledSelect
                  key={f.param}
                  label={f.label}
                  value={values[f.param] || ''}
                  onChange={v => setValues(s => ({...s, [f.param]: v}))}
                  cls={selectCls}
                >
                  {/* "All" as the search page's Sel shows when nothing is picked */}
                  <option value="">{trans({message: 'All'})}</option>
                  {f.options.map(o => (
                    <option key={o.value} value={o.value}>{trans({message: o.label})}</option>
                  ))}
                </LabeledSelect>
              ),
            )}
          </div>

          <div className="flex justify-center pt-4">
            <Button type="submit" variant="flat" color="primary" size="lg" className="rounded-xl px-40">
              <span className="material-icons me-6 text-base">search</span>
              <Trans message="Search Now" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function LabeledSelect({
  label,
  value,
  onChange,
  cls,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  cls: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-4">
      <span className="text-xs font-medium text-muted"><Trans message={label} /></span>
      <select value={value} onChange={e => onChange(e.target.value)} className={cls}>
        {children}
      </select>
    </label>
  );
}
